// checkout/init.ts

import { populateForm } from './utils/populateForm';
import { addLoginOrRegisterListeners } from './steps/loginOrRegisterListeners';
import { addAccountDetailsListeners } from './steps/accountDetailsListeners';
import { addDeliveryAddressListeners } from './steps/deliveryAddressListeners';
import { addPaymentMethodListeners } from './steps/paymentMethodListeners';
import { updateFormStatusClass } from './utils/formStatus';
import { renderPaymentOptions } from './steps/paymentMethodRenderer';
import { initialiseCheckoutListeners } from './eventListeners';
import { addReviewAndConfirmListeners } from './steps/reviewAndConfirmListeners';
import { loadWhat3WordsScript } from './utils/loadWhat3WordsScript';
import { addBackForwardListener } from './utils/addBackForwardListener';
import { initHiddenRows, toggleFormRow } from './utils/toggleFormRows';
import { executeStepFocusCallbacks } from './utils/stepFocusCallbacks';
import { handleModalToggling } from '../modals/modalListeners';

export const checkoutData: Record<string, any> = {};

/**
 * Parses all JSON <script> blocks on page into the shared checkoutData object.
 */
export function updateCheckoutData(): void {
	const scriptTags = document.querySelectorAll<HTMLScriptElement>('script[id^="step-json-"]');

	scriptTags.forEach((script) => {
		try {
			const stepId = script.id.replace('step-json-', '');
			const json = JSON.parse(script.textContent || '{}');
			checkoutData[stepId] = json;
		} catch (error) {
			console.error(`❌ Failed to parse JSON from <script id="${script.id}">`, error);
		}
	});

	//console.log('✅ checkoutData initialised with full contents:\n', checkoutData);
}

/**
 * Populates all forms with data from checkoutData.
 */
function populateForms(): void {
	const register = checkoutData.register;
	if (register?.fields) {
		populateForm('registrationForm', register.fields);
	}

	const login = checkoutData.login;
	if (login?.fields) {
		populateForm('loginForm', login.fields);
	}

	const account = checkoutData.accountDetails;
	if (account?.fields) {
		populateForm('accountDetailsForm', account.fields, {
			title: account.titles || [],
		});
	}

	const delivery = checkoutData.deliveryAddress;
	if (delivery?.fields) {
		const isDigitalOnly = delivery.isDigitalOnly === 'true';

		populateForm('deliveryAddressForm', delivery.fields, {
			'deliveryAddress.address.title': delivery.titles || [],
			'deliveryAddress.address.countryCode': (delivery.countries || [])
				.filter((c: any) => isDigitalOnly || c.isAllowedForDelivery === 'true')
				.map((c: any) => ({ value: c.code, text: c.name })),
			'deliveryAddress.deliveryInstructionId': (delivery.deliveryInstructions || []).map(
				(d: any) => ({
					value: d.id,
					text: d.description,
				}),
			),
		});

		// Open manual address fields if populated
		if (delivery?.fields['deliveryAddress.address.addressLine1'] !== '') {
			const form = document.getElementById('deliveryAddressForm') as HTMLFormElement | null;
			if (form) {
				const btn = form.querySelector<HTMLElement>('.manual-address-entry');
				if (btn) toggleFormRow(btn);
			}
		}
	}

	const paymentMethod = checkoutData.paymentMethod;
	if (paymentMethod?.fields) {
		populateForm('paymentMethodForm', paymentMethod.fields);
		renderPaymentOptions(paymentMethod);
	}
}

/**
 * Orchestrates full page initialisation.
 */
export function initialise(): void {
	initHiddenRows();
	updateCheckoutData();

	populateForms();

	updateFormStatusClass('loginForm', 'login');
	updateFormStatusClass('accountDetailsForm', 'accountDetails');
	updateFormStatusClass('deliveryAddressForm', 'deliveryAddress');
	updateFormStatusClass('paymentMethodForm', 'paymentMethod');

	addLoginOrRegisterListeners();
	addAccountDetailsListeners();
	addDeliveryAddressListeners();
	addPaymentMethodListeners();
	addReviewAndConfirmListeners();
	addBackForwardListener();
	handleModalToggling();

	initialiseCheckoutListeners();

	Object.entries(checkoutData).forEach(([stepId, stepData]) => {
		if (stepData?.status === 'FOCUSED') {
			executeStepFocusCallbacks(stepId, 'FOCUSED');
		}
	});

	if (window.serverData?.w3wEnabled) {
		loadWhat3WordsScript();
	}
}
